import type {
  PaginationState,
  SortingState,
  Updater,
} from "@tanstack/react-table";

import { useMemo } from "react";

import type { ReturnedTranslatedContentClientsPageHook } from "../../types";

import { useIsMobile } from "../../../hooks";
import { TableDesktop, TableMobile } from "../../../Table";
import { useClientTradingReportContext } from "../providers/TradingReportProvider";
import { clientTradingColumns } from "./Columns";
import { MobileCardBody, MobileCardHeader } from "./MobileCard";

export type ClientTradingTableProps = {
  isBackoffice?: boolean;
  translations: ReturnedTranslatedContentClientsPageHook;
  showOfferIdColumn?: boolean;
  onSharedProgramClick?: (hasOfferID: string) => void;
};

export const ClientTradingTable = ({
  translations,
  isBackoffice = false,
  showOfferIdColumn = false,
  onSharedProgramClick,
}: ClientTradingTableProps) => {
  const { translatedContent, language } = translations;
  const {
    state: { page, pageSize, sortBy, sortOrder },
    resources: { table },
    actions: { setSort, setPage },
    runResource,
  } = useClientTradingReportContext();

  const { error } = table;
  const { tradingList = [], redactedFields, totalCount } = table.data ?? {};
  const isLoading = table.status === "loading";

  const columnVisibility = useMemo(() => {
    return redactedFields?.length
      ? redactedFields.reduce<Record<string, boolean>>(
          (acc: Record<string, boolean>, field: string) => {
            acc[field] = false;
            return acc;
          },
          {},
        )
      : { clientId: false };
  }, [redactedFields]);

  const isMobile = useIsMobile();

  const sorting = useMemo(
    () =>
      sortBy != null && sortOrder != null
        ? [{ desc: sortOrder === "desc", id: sortBy }]
        : [],
    [sortOrder, sortBy],
  );

  const handleSortingUpdate = (updater: Updater<SortingState>) => {
    const nextSorting =
      typeof updater === "function" ? updater(sorting) : updater;
    const newSort = nextSorting[0];
    if (newSort.id !== sortBy) {
      setSort?.(newSort.id, "desc");
    } else {
      setSort?.(nextSorting[0].id, nextSorting[0].desc ? "desc" : "asc");
    }
  };

  const tableConfig = useMemo(
    () => ({
      translatedContent: translatedContent.tradingReportTab.tradingColumnLabels,
      language,
      columnVisibility,
      showOfferIdColumn,
      onSharedProgramClick,
    }),
    [
      translatedContent,
      language,
      columnVisibility,
      showOfferIdColumn,
      onSharedProgramClick,
    ],
  );

  const columns = clientTradingColumns({
    ...tableConfig,
    redactedFields,
    isBackoffice,
    showOfferIdColumn,
  });

  // Handle pagination changes for server-side mode
  const handlePaginationChange = (
    updater: PaginationState | ((old: PaginationState) => PaginationState),
  ) => {
    const newPagination =
      typeof updater === "function"
        ? updater({
            pageIndex: page,
            pageSize,
          })
        : updater;

    setPage(newPagination.pageIndex);
  };

  const pageCount = Math.ceil((totalCount ?? 0) / (pageSize ?? 10));

  if (isMobile) {
    return (
      <TableMobile
        data={tradingList}
        onRetry={() => runResource("table")}
        isError={Boolean(error)}
        columns={columns}
        cardHeader={MobileCardHeader(tableConfig)}
        cardBody={MobileCardBody(tableConfig)}
        showPagination
        translatedContent={translatedContent}
        filterFns={{} as any} // No client-side filter functions
        // Server-side pagination props
        enableServerSidePagination={true}
        pageCount={pageCount}
        totalCount={totalCount}
        state={{ pagination: { pageIndex: page, pageSize } }}
        onPaginationChange={handlePaginationChange}
      />
    );
  }

  return (
    <TableDesktop
      data={tradingList}
      columns={columns}
      onRetry={() => runResource("table")}
      isError={Boolean(error)}
      showPagination
      translatedContent={translatedContent}
      enableSorting
      enabledServerSideSorting
      sorting={sorting}
      onSortingChange={handleSortingUpdate}
      pageSize={pageSize}
      isLoading={isLoading}
      filterFns={{} as any} // No client-side filter functions
      showHorizontalScroll
      stickyFirstColumn
      scrollFrame
      // Server-side pagination props
      enableServerSidePagination={true}
      pageCount={pageCount}
      totalCount={totalCount}
      state={{ pagination: { pageIndex: page, pageSize } }}
      onPaginationChange={handlePaginationChange}
    />
  );
};
